import React, { useState } from "react";
import {
  Container,
  Typography,
  Box,
  Card,
  CardContent,
  Collapse,
  Divider,
  IconButton,
  Grid,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { styled } from "@mui/material/styles";

// MUI v7-compatible styled icon
const ExpandMore = styled(IconButton, {
  shouldForwardProp: (prop) => prop !== "expand",
})(({ expand, theme }) => ({
  transform: expand ? "rotate(180deg)" : "rotate(0deg)",
  transition: theme.transitions.create("transform", {
    duration: theme.transitions.duration.shortest,
  }),
}));

const FeatureCard = ({ title, preview, details, expanded, onToggle }) => {
  return (
    <Card
      onClick={onToggle}
      sx={{
        width: 260,
        backgroundColor: "info.light",
        cursor: "pointer",
        transition: "all 0.3s ease",
        display: "flex",
        flexDirection: "column",
        justifyContent: "space-between",
        "&:hover": {
          boxShadow: 6,
          transform: "scale(1.02)",
        },
      }}
    >
      <CardContent sx={{ flexGrow: 1 }}>
        <Box
          sx={{
            display: "flex",
            flexGrow: 1,
            flexDirection: "row",
            justifyContent: "space-between",
            alignItems: "flex-start",
            minHeight: 150,
          }}
        >
          <Box>
            <Typography variant="h6" color="secondary.contrastText" gutterBottom>
              {title}
            </Typography>
            <Typography variant="body1" color="secondary.contrastText">
              {preview}
            </Typography>
          </Box>
          <ExpandMore
            expand={expanded}
            aria-expanded={expanded}
            aria-label="show more"
          >
            <ExpandMoreIcon />
          </ExpandMore>
        </Box>
        <Collapse in={expanded} timeout="auto" unmountOnExit>
          <Typography variant="body1" sx={{ mt: 1 }} color="secondary.contrastText">
            {details}
          </Typography>
        </Collapse>
      </CardContent>
    </Card>
  );
};

const FeaturesSection = () => {
  const [expandedCards, setExpandedCards] = useState([]);

  const toggleCard = (index) => {
    setExpandedCards((prev) =>
      prev.includes(index) ? prev.filter((i) => i !== index) : [...prev, index]
    );
  };

  return (
    <Container id="featuresSection" maxWidth="lg" sx={{ mt: 4 }}>
      <Box sx={{ px: 2, mt: 4, mb: 4 }}>
          <Typography
            variant="h4"
            component="h2"
            gutterBottom
            color="primary"
            align="center"
          >
            Platform Features
          </Typography>
      </Box>

      <Typography variant="body1" align="center" sx={{ mb: 4 }} color="text.secondary">
        Unsere Plattform wurde entwickelt, um juristische Recherchen im Bereich
        Datenschutzrecht effizienter, intelligenter und benutzerfreundlicher zu
        gestalten. Hier sind einige der wichtigsten Funktionen:
      </Typography>

      <Grid container spacing={4} justifyContent="space-evenly">
        {[0, 1, 2].map((idx) => {
          const featureProps = [
            {
              title: "1. Kontextbasierte Suche",
              preview:
                "Stelle Fragen in natürlicher Sprache und erhalte relevante Antworten.",
              details:
                "Unsere Plattform nutzt semantische Algorithmen, um Ihre Anfrage im rechtlichen Kontext zu interpretieren. Sie finden so nicht nur Treffer nach Schlagworten, sondern echte Zusammenhänge aus der DSGVO.",
            },
            {
              title: "2. DSGVO-Datenbankzugriff",
              preview:
                "Greife auf eine kuratierte Datenbank von DSGVO-relevanten Entscheidungen zu.",
              details:
                "Unsere Inhalte sind handverlesen und stammen aus offiziellen Quellen. Sie können Entscheidungen nach Datum, Thema oder betroffener Personengruppe filtern und durchsuchen.",
            },
            {
              title: "3. Nutzerfreundliches Interface",
              preview:
                "Ein intuitives Design hilft dir, schnell das Gewünschte zu finden.",
              details:
                "Dank klarer Struktur, dunkler und heller Modusoptionen sowie mobiler Optimierung finden sich auch Nicht-Juristen schnell zurecht. Ideal für Unternehmen, Kanzleien und Datenschutzbeauftragte.",
            },
          ][idx];

          return (
            <Grid item xs={12} sm={6} md={4} key={idx}>
              <FeatureCard
                {...featureProps}
                expanded={expandedCards.includes(idx)}
                onToggle={() => toggleCard(idx)}
              />
            </Grid>
          );
        })}
      </Grid>
    </Container>
  );
};

export default FeaturesSection;
